// components/public/publicSiteData.js
import { getPublicPageDefinition } from './publicSiteRegistry.mjs';

export const BRAND = {
  name: 'TaskerSys',
  legalName: 'Copala Inc.',
  phoneDisplay: '718-374-2603',
  phoneHref: 'tel:+17183742603',
  email: 'info@superteamusa.com',
  emailContext:
    'Current operations mailbox for TaskerSys and Super Team field-service inquiries',
  address: '330 East 49th St, New York, NY 10017',
  addressContext:
    'Please coordinate before visiting; the address is not presented as a walk-in service counter.',
  serviceArea: 'New York City, including the five boroughs',
  blue: '#4b9ef1',
  relationship:
    'TaskerSys is operated by Copala Inc. The TaskerSys platform and TaskerSys Field Services are presented as separate ways to engage, while current field-service inquiries are handled through the Super Team operations mailbox.',
};

export const CLIENT_PROFILES = [
  {
    title: 'Managers taking over additional buildings',
    text:
      'Support for vacant units, unfinished repairs, common-area needs, and work identified during a management transition.',
    signal:
      'A named building, confirmed transition, approved improvement program, or defined backlog makes the opportunity stronger.',
  },
  {
    title: 'Growing managers that outsource field work',
    text:
      'Additional execution capacity for repeat turnovers, repairs, cleaning, painting, flooring, and coordinated scopes.',
    signal:
      'The best fit has active buildings, repeat work, an accessible decision maker, and properties that can be served efficiently.',
  },
  {
    title: 'Established teams with a project-capacity gap',
    text:
      'Contained support for concentrated backlogs, simultaneous turnovers, common-area programs, and multi-trade work.',
    signal:
      'TaskerSys can complement in-house staff and regular vendors without asking the client to replace relationships that already work.',
  },
];

export const ENGAGEMENT_PRINCIPLES = [
  {
    number: '01',
    title: 'Start with a defined assignment',
    text:
      'Use one apartment, one repair list, one common-area scope, one project phase, or one workflow as a practical starting point.',
  },
  {
    number: '02',
    title: 'Work alongside the existing operation',
    text:
      'Keep effective building staff, managers, vendors, and specialty trades in place while adding capacity where a specific gap exists.',
  },
  {
    number: '03',
    title: 'Agree responsibilities before execution',
    text:
      'Clarify access, materials, approvals, scope boundaries, reporting, pricing method, and the person authorized to make decisions.',
  },
  {
    number: '04',
    title: 'Close out with evidence',
    text:
      'Finish with photographs, completion notes, remaining items, approvals, and the financial records agreed for the assignment.',
  },
];

export const WORKFLOW_STEPS = [
  {
    number: '01',
    title: 'Define the work',
    text: 'Scope, property context, access, timing, priorities, and approval path.',
  },
  {
    number: '02',
    title: 'Coordinate the team',
    text: 'Assignments, dependencies, materials, building rules, and schedule.',
  },
  {
    number: '03',
    title: 'Keep everyone informed',
    text: 'Questions, progress, photographs, approvals, and issue visibility.',
  },
  {
    number: '04',
    title: 'Close out with clarity',
    text: 'Completion notes, remaining items, photographs, and financial records.',
  },
];

export const PLATFORM_ROLES = [
  {
    title: 'Property Management Companies',
    what:
      'Centralized control for client service requests, permissions, buildings, managers, service providers, and field resources.',
    how:
      'Supports portfolio-wide visibility, resource allocation, structured vendor coordination, and consistent closeout.',
  },
  {
    title: 'Property Owners',
    what: 'A direct way to create service requests and monitor progress.',
    how:
      'Improves transparency, response visibility, and the tracking of property-related work.',
  },
  {
    title: 'Service Providers',
    what:
      'A structured workspace for receiving and managing service requests while collaborating with managers and dispatchers.',
    how:
      'Clarifies scope, assignments, updates, field execution, and completion records.',
  },
  {
    title: 'Dispatching Services',
    what:
      'Tools to assign, schedule, coordinate, and track field specialists, technicians, and subcontractors.',
    how:
      'Helps dispatchers manage resources independently while remaining connected to the larger property workflow.',
  },
  {
    title: 'Subcontractors',
    what: 'Clear task details and a direct connection to the dispatcher or main contractor.',
    how:
      'Reduces ambiguity, improves communication, and makes each assignment easier to complete and document.',
  },
  {
    title: 'Technicians',
    what: 'Immediate access to task details, scheduling, location context, and updates.',
    how:
      'Supports field productivity and a clean handoff between planning, execution, and closeout.',
  },
];

export const PLATFORM_CAPABILITIES = [
  {
    title: 'Requests, tickets, and projects',
    text:
      'Capture service requests, property context, scope, priorities, dates, budgets, media, and completion information.',
  },
  {
    title: 'People, companies, and permissions',
    text:
      'Connect clients, managers, general managers, service providers, dispatchers, subcontractors, technicians, and building teams.',
  },
  {
    title: 'Scheduling and field coordination',
    text:
      'Assign work, manage implementers, coordinate site access, record time, and keep operational handoffs visible.',
  },
  {
    title: 'Photos, updates, and approvals',
    text:
      'Keep progress media, comments, questions, approval decisions, and closeout documentation attached to the work.',
  },
  {
    title: 'Budgets and financial workflow',
    text:
      'Track project budgets, labor allocations, materials, invoices, receivables, payables, payroll, and financial reporting.',
  },
  {
    title: 'Portfolio and performance visibility',
    text:
      'Review work across buildings, teams, tags, clients, vendors, service categories, and reporting periods.',
  },
];

export const SERVICE_PAGES = {
  'apartment-turnovers-nyc': {
    slug: 'apartment-turnovers-nyc',
    shortTitle: 'Apartment turnovers',
    title: 'NYC apartment turnovers and make-ready support',
    eyebrow: 'APARTMENT TURNOVERS',
    description:
      'Apartment turnover support for NYC property managers, including punchlists, patching, painting, small carpentry, flooring repairs, cleaning, final readiness checks, and photo-based closeout.',
    lead:
      'Add organized field capacity when vacant units, simultaneous turnovers, or accumulated punchlists exceed the building team’s available time.',
    audience: 'Property managers, owners, rental operators, and portfolio teams',
    summary:
      'The goal is not renovation for its own sake. The goal is to move a vacant apartment from identified work to a documented, rent-ready condition with fewer handoffs and less follow-up.',
    scope: [
      'Vacant-unit walkthroughs, readiness lists, and punchlist execution',
      'Wall repair, patching, touch-up painting, and full repainting where scoped',
      'Doors, hardware, shelving, fixtures, trim, and small carpentry items',
      'Localized flooring repair, replacement, and coordination of larger flooring scopes',
      'Kitchen and bathroom readiness items that do not require a separate major renovation',
      'Turnover and final-detail cleaning',
      'Before, progress, and after photographs',
      'Completion notes and remaining-item reporting',
    ],
    buyingOptions: [
      'One pilot unit or one punchlist package',
      'Repeat per-turnover scopes with an agreed standard',
      'Fixed-price work where the condition and scope are sufficiently defined',
      'Labor plus reimbursable materials, included-material, or allowance structures',
      'Concentrated multi-unit programs with phased scheduling',
    ],
    pricingReferences: [
      'Fixed-price proposals are available when the unit condition, inclusions, exclusions, and material responsibility are sufficiently defined.',
      'Repeat-turnover standards can be priced after a pilot establishes the expected scope, quality, access, and closeout requirements.',
      'Labor-only, reimbursable-material, included-material, and allowance structures can be documented in the proposal.',
    ],
    pricingNote:
      'Final pricing depends on condition, exact scope, access, scheduling, material requirements, quantity, building rules, and the approval structure. Website language is not a binding offer.',
    whatToSend: [
      'Property address and apartment identifier',
      'Photographs, inspection notes, or an existing punchlist',
      'Target rent-ready or inspection date',
      'Access procedure and building work-hour rules',
      'Known material standards and items supplied by the client',
      'The person authorized to approve changes',
    ],
    faqs: [
      {
        q: 'Can TaskerSys begin with one apartment?',
        a: 'Yes. A single defined turnover is the preferred way to establish scope clarity, communication expectations, quality, timing, and commercial fit before expanding.',
      },
      {
        q: 'Can you coordinate several trades in the same unit?',
        a: 'Yes. Turnover work can combine cleaning, patching, painting, carpentry, hardware, fixtures, flooring repairs, and related readiness items, subject to the final agreed scope.',
      },
      {
        q: 'Do you provide completion documentation?',
        a: 'The scope can include before-and-after photographs, progress updates, completion notes, remaining-item reporting, and closeout records in TaskerSys or the client’s preferred workflow.',
      },
    ],
    related: ['property-maintenance-nyc', 'property-cleaning-nyc', 'renovation-nyc'],
  },
  'property-maintenance-nyc': {
    slug: 'property-maintenance-nyc',
    shortTitle: 'Maintenance & repairs',
    title: 'NYC property maintenance, repair backlogs, and project capacity',
    eyebrow: 'MAINTENANCE & REPAIRS',
    description:
      'Property maintenance and repair support for NYC managers, including defined punchlists, repair backlogs, common-area work, multi-trade assignments, and additional crews for concentrated scopes.',
    lead:
      'Use TaskerSys when routine building staff and existing vendors cover daily operations but additional work still needs a dependable owner, schedule, and closeout path.',
    audience: 'Property managers, building owners, operations teams, and in-house maintenance departments',
    summary:
      'This service is designed for recurring work that falls between an individual handyman visit and a large general-contractor project—or for times when the normal team needs added capacity.',
    scope: [
      'Defined repair lists across units, common areas, and building support spaces',
      'Patching, painting, trim, doors, hardware, fixtures, and finish repairs',
      'Localized flooring and wall repairs',
      'Corridor, lobby, stairwell, laundry-room, and shared-space work',
      'Exterior and backyard items where the scope and access are suitable',
      'Multi-location punchlists and recurring building assignments',
      'Coordination of specialty trades when included in the agreed project structure',
      'Progress photographs, issue reporting, and closeout records',
    ],
    buyingOptions: [
      'One contained repair or punchlist package',
      'Recurring building support with agreed service standards',
      'Fixed-price proposals for defined scopes',
      'Not-to-exceed structures where conditions may require controlled flexibility',
      'Phased programs across several units or common areas',
    ],
    pricingReferences: [
      'Defined repair lists can be proposed at a fixed price when quantities, access, exclusions, and material responsibility are clear.',
      'Recurring maintenance, multi-location punchlists, and uncertain conditions may use agreed hourly, day-rate, unit-rate, or not-to-exceed structures.',
      'Multi-trade assignments are priced from the combined scope, sequencing, supervision, logistics, and closeout requirements.',
    ],
    pricingNote:
      'There is no universal public maintenance rate. The proposal should identify labor, materials, minimum mobilization, exclusions, schedule assumptions, and approval requirements.',
    whatToSend: [
      'Building address and the affected areas',
      'A repair list, scope, photographs, or inspection report',
      'Priority order and target completion date',
      'Access, tenant, superintendent, and work-hour requirements',
      'Known dependencies, permits, specialty trades, or owner approvals',
      'The person authorized to approve changes and costs',
    ],
    faqs: [
      {
        q: 'Do you replace a building’s existing maintenance team?',
        a: 'Not necessarily. Many assignments are specifically designed to supplement an existing superintendent, maintenance staff, or vendor network during backlogs, transitions, or concentrated projects.',
      },
      {
        q: 'Can one assignment cover several different repair types?',
        a: 'Yes. The strongest fit is often a combined list that needs one responsible coordinator rather than several disconnected small vendors.',
      },
      {
        q: 'Can work be phased around an occupied property?',
        a: 'Yes, when the scope, access, safety requirements, tenant communication, and permitted work hours are clear before mobilization.',
      },
    ],
    related: ['apartment-turnovers-nyc', 'renovation-nyc', 'property-cleaning-nyc'],
  },
  'property-cleaning-nyc': {
    slug: 'property-cleaning-nyc',
    shortTitle: 'Property cleaning',
    title: 'NYC turnover, post-renovation, shared-apartment, and common-area cleaning',
    eyebrow: 'PROPERTY CLEANING',
    description:
      'Cleaning services for NYC property operations, including turnover cleaning, post-renovation cleaning, shared-apartment cleaning, common-area cleaning, final detail work, and photo-based completion reporting.',
    lead:
      'Keep vacant units, occupied shared apartments, and building common areas presentable and ready on a dependable schedule.',
    audience: 'Property managers, landlords, rental operators, furnished-housing teams, and hotel operators',
    summary:
      'Cleaning is treated as an operational scope with defined areas, condition, frequency, access, completion standards, and documentation—not as an undefined one-time request.',
    scope: [
      'Turnover cleaning between tenants or occupants',
      'Post-repair and post-renovation dust and detail cleaning',
      'Shared-apartment kitchens, bathrooms, living spaces, and common-use areas',
      'Lobbies, corridors, stairwells, entry areas, laundry rooms, and amenity spaces',
      'Appliance-interior cleaning when included',
      'Light trash-out and room-reset support when scoped',
      'Recurring or project-based schedules',
      'Completion photographs and reporting where requested',
    ],
    buyingOptions: [
      'One turnover or post-project cleaning assignment',
      'Per-room or per-unit repeat standards',
      'Recurring shared-apartment or common-area schedules',
      'Condition-based pricing for heavier cleaning',
      'Cleaning combined with repair, paint, furnishing, or turnover work',
    ],
    pricingReferences: [
      'Turnover and post-renovation cleaning can be priced per unit, per room, per defined area, or as a fixed project scope after condition review.',
      'Recurring shared-apartment and common-area programs can use agreed frequencies and service standards after a pilot or walkthrough.',
      'Supply responsibility, appliance interiors, trash-out, linen/reset work, access, photographs, and reporting are identified separately where applicable.',
    ],
    pricingNote:
      'Final pricing depends on actual condition, area, room count, frequency, access, quantity, supplies, service standard, and documentation requirements. Website language is not a binding offer.',
    whatToSend: [
      'Property address and the rooms or areas to be cleaned',
      'Current photographs and approximate square footage or room count',
      'Occupied or vacant status',
      'The required completion standard and target date',
      'Access instructions and building rules',
      'Any appliance interiors, trash-out, linen, reset, or photo requirements',
    ],
    faqs: [
      {
        q: 'Can cleaning be combined with turnover repairs?',
        a: 'Yes. Cleaning can be scheduled as the final part of a turnover, repair, painting, flooring, furnishing, or renovation scope.',
      },
      {
        q: 'Do you handle recurring common-area cleaning?',
        a: 'Recurring work can be evaluated when the locations, frequency, access, service standard, supply responsibility, and commercial structure are clear.',
      },
      {
        q: 'Can the team provide completion photographs?',
        a: 'Yes. Photo-based completion reporting can be included where it is useful for remote managers, owners, or portfolio teams.',
      },
    ],
    related: ['apartment-turnovers-nyc', 'property-maintenance-nyc', 'renovation-nyc'],
  },
  'renovation-nyc': {
    slug: 'renovation-nyc',
    shortTitle: 'Renovation & refresh',
    title: 'NYC apartment, common-area, and property refresh projects',
    eyebrow: 'RENOVATION & REFRESH',
    description:
      'Defined renovation and refresh projects for NYC apartments and common areas, including painting, flooring, drywall, finish carpentry, doors, hardware, fixtures, and coordinated multi-trade execution.',
    lead:
      'Use a structured project approach for scopes that are larger than routine maintenance but do not require the complexity of a major ground-up construction engagement.',
    audience: 'Property managers, owners, co-op and condo stakeholders, rental operators, and building operations teams',
    summary:
      'The strongest projects have a defined result, an approved decision path, workable access, realistic scheduling, and clear responsibility for design, permits, finishes, specialty trades, and owner-supplied items.',
    scope: [
      'Interior demolition and preparation where included',
      'Drywall, patching, plaster repair, and wall preparation',
      'Painting and finish-coat programs',
      'Flooring replacement, repair, transitions, and protection',
      'Doors, trim, moldings, hardware, shelving, and finish carpentry',
      'Kitchen, bathroom, apartment, corridor, lobby, and shared-space refresh work',
      'Coordination of specialty trades and client-supplied finishes where agreed',
      'Progress reporting, photographs, punchlists, and closeout',
    ],
    buyingOptions: [
      'One apartment or one common-area pilot project',
      'Labor-only proposals when the client supplies finishes and materials',
      'Labor-plus-materials or turnkey structures where appropriate',
      'Fixed-price proposals for sufficiently defined scopes',
      'Phased or not-to-exceed structures for controlled uncertainty',
    ],
    pricingReferences: [
      'Renovation pricing is prepared from the actual scope, drawings, site condition, access plan, schedule, finish responsibility, permit responsibility, and exclusions.',
      'Fixed-price, labor-only, labor-plus-materials, phased, and not-to-exceed structures can be used when they fit the project conditions.',
    ],
    pricingNote:
      'A reliable renovation proposal requires a clear division between labor, standard materials, finishes, appliances, fixtures, permits, design, building fees, specialty trades, and owner-provided items.',
    whatToSend: [
      'Address, property type, and occupied or vacant status',
      'Drawings, scope, finish schedule, photographs, or inspection notes',
      'Building alteration rules and permitted work hours',
      'Target schedule and phasing requirements',
      'Responsibility for permits, design, finishes, appliances, and specialty trades',
      'The approval and payment structure',
    ],
    faqs: [
      {
        q: 'Can TaskerSys provide a labor-only proposal?',
        a: 'Yes, when the client’s responsibility for materials, finishes, delivery, design, and specialty items is clearly defined.',
      },
      {
        q: 'Can a project be divided into phases?',
        a: 'Yes. Phasing is useful for occupied properties, repeated units, corridor programs, floor-by-floor work, or projects that must coordinate with owner approvals and material availability.',
      },
      {
        q: 'How are change orders handled?',
        a: 'Additional work should be documented with a clear scope, price or pricing method, schedule impact, and approval before execution whenever conditions permit.',
      },
    ],
    related: ['property-maintenance-nyc', 'apartment-turnovers-nyc', 'hotel-renovation'],
  },
  'hotel-renovation': {
    slug: 'hotel-renovation',
    shortTitle: 'Hotel renovation',
    title: 'Hotel guest-room and common-space renovation support',
    eyebrow: 'HOTEL RENOVATION',
    description:
      'Hotel guest-room and common-area renovation support with phased scheduling, multi-trade coordination, progress reporting, punchlist management, and closeout planning for operating properties.',
    lead:
      'Plan guest-room, corridor, lobby, and shared-space improvements around occupancy, operational constraints, material readiness, brand standards, and controlled handoffs.',
    audience: 'Hotel owners, operators, asset managers, property managers, and project teams',
    summary:
      'Hotel work requires disciplined phasing and clear communication because construction must coexist with operations, guests, housekeeping, engineering, ownership, and brand requirements.',
    scope: [
      'Guest-room demolition, wall repair, painting, flooring, trim, doors, and hardware',
      'Bathroom updates, fixtures, accessories, and finish work where scoped',
      'Corridors, lobbies, reception areas, amenity rooms, meeting rooms, and shared interiors',
      'FF&E installation and room-reset support when included',
      'Floor-by-floor or room-block phasing',
      'Daily or milestone progress reporting',
      'Issue logs, photographs, punchlists, and completion records',
      'Post-construction cleaning and operational handoff when included',
    ],
    buyingOptions: [
      'One model room or limited pilot phase',
      'Labor-only execution with client-provided materials and finishes',
      'Labor-plus-materials or more complete project structures',
      'Fixed-price, phased, or not-to-exceed proposals',
      'Repeat room blocks with an agreed standard and closeout process',
    ],
    pricingReferences: [
      'A model room or limited room block can establish production assumptions before a larger room release is priced.',
      'Labor-only, labor-plus-materials, unit-rate, phased, or more complete proposals can be structured depending on ownership preference and project conditions.',
      'Room type, bathroom scope, occupancy, logistics, phasing, work hours, material readiness, and closeout standards are priced explicitly.',
    ],
    pricingNote:
      'There is no universal per-room website price. A reliable proposal requires room types, quantities, current conditions, brand standards, material responsibility, operating constraints, and reporting requirements.',
    whatToSend: [
      'Property address, room count, room types, and areas included',
      'Drawings, brand standards, finish schedules, and sample-room requirements',
      'Occupancy and phasing constraints',
      'Work-hour, freight-elevator, loading, protection, and housekeeping coordination rules',
      'Material procurement and FF&E responsibility',
      'Reporting, punchlist, and handoff requirements',
    ],
    faqs: [
      {
        q: 'Can you begin with a model room?',
        a: 'Yes. A model room or limited room block can establish scope, quality, sequence, reporting, logistics, and production assumptions before a larger release.',
      },
      {
        q: 'Can work be phased while the hotel remains open?',
        a: 'Yes, provided the operating constraints, room releases, access, protection, noise rules, material flow, housekeeping coordination, and emergency procedures are agreed in advance.',
      },
      {
        q: 'Do you provide punchlist and progress reporting?',
        a: 'The project structure can include room-by-room progress, photographs, issue logs, milestone reporting, punchlists, and completion records.',
      },
    ],
    related: ['renovation-nyc', 'property-cleaning-nyc', 'property-maintenance-nyc'],
  },
};


// Keep each service page's public metadata and hero copy synchronized with the
// shared frontend/backend registry. Scope, pricing references, FAQs, and related
// commercial structure, FAQs, and related service data remain in this file.
for (const service of Object.values(SERVICE_PAGES)) {
  const page = getPublicPageDefinition(`/services/${service.slug}`);
  if (!page) continue;

  service.title = page.heading;
  service.eyebrow = page.eyebrow;
  service.description = page.description;
  service.lead = page.lead;
}

export const SERVICE_ORDER = [
  'apartment-turnovers-nyc',
  'property-maintenance-nyc',
  'property-cleaning-nyc',
  'renovation-nyc',
  'hotel-renovation',
];

export const SERVICES = SERVICE_ORDER.map((slug) => SERVICE_PAGES[slug]);

export const SERVICE_ICON_KEYS = {
  'apartment-turnovers-nyc': 'home',
  'property-maintenance-nyc': 'build',
  'property-cleaning-nyc': 'clean',
  'renovation-nyc': 'layers',
  'hotel-renovation': 'hotel',
};

